import { useMutation } from '@apollo/client';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { UpdateRiskAssessmentResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateRiskAssessmentResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateRiskAssessmentResultTRPC } from './useUpdateRiskAssessmentResultTRPC';

type UpdateRiskAssessmentResultInput = {
  Id: string;
  Rating?: number | null;
  Likelihood?: number | null;
  Impact?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: Record<string, unknown> | null;
  AssessmentId?: string | null;
  OriginalTimestamp: string;
  ParentRiskId?: string | null;
};

export const useUpdateRiskAssessmentResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const { addNotification } = useNotifications();

  // GraphQL mutation
  const [updateRiskAssessmentResultGraphQL, graphqlState] = useMutation(
    UpdateRiskAssessmentResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk_assessment_result');
        evictField(cache, 'assessment');
        evictField(cache, 'risk_assessment_result_aggregate');
      },
      onError: (error) => {
        if (!trpcEnabled) {
          addNotification({
            type: 'error',
            content: error.message,
          });
        }
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateRiskAssessmentResultTRPC();

  const updateRiskAssessmentResult = async (
    variables: UpdateRiskAssessmentResultInput
  ): Promise<UpdateRiskAssessmentResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateRiskAssessmentResult(variables);
    }

    const result = await updateRiskAssessmentResultGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to update risk assessment result');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateRiskAssessmentResult,
      isLoading: trpcMutation.isLoading,
    };
  }

  return {
    updateRiskAssessmentResult,
    isLoading: graphqlState.loading,
  };
};
